import { Socket } from 'socket.io-client';
import { logger } from '../utils/logger';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { ApiService } from './api-services/ApiService';

interface RawNotificationResponse {
  id: number | string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  createdAt?: string;
}

interface MessagePayload {
  id: string | number;
  senderId: string | number;
  receiverId: string | number;
  content: string;
  timestamp: string;
  encrypted?: boolean;
  messageType?: 'TEXT' | 'IMAGE' | 'FILE' | 'AUDIO' | 'VIDEO';
}

export interface NotificationPayload {
  id: string;
  userId: string | number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt?: string;
}

interface UserPresencePayload {
  userId: string | number;
  status: 'ONLINE' | 'OFFLINE' | 'AWAY';
  role?: string;
  lastSeen?: string;
}

interface CallPayload {
  callerId: string | number;
  receiverId: string | number;
  type: 'audio' | 'video';
  consultationId?: string;
  timestamp: string;
}

/**
 * Service unifié frontend pour la gestion des notifications et messages
 * Centralise: Socket.IO, Notifications, Messages, Presence
 */
export class UnifiedNotificationService {
  private static instance: UnifiedNotificationService;
  private socket: Socket | null = null;
  private notifications: NotificationPayload[] = [];
  private listeners: Map<string, ((...args: unknown[]) => void)[]> = new Map();
  private userId: string | number | null = null;

  private constructor() {
    this.initializeEventListeners();
  }

  static getInstance(): UnifiedNotificationService {
    if (!this.instance) {
      this.instance = new UnifiedNotificationService();
    }
    return this.instance;
  }

  static initialize(socket: Socket, userId: string | number): void {
    const service = this.getInstance();
    service.socket = socket;
    service.userId = userId;
    service.setupSocketListeners();
    logger.info('🚀 UnifiedNotificationService Frontend initialisé', { userId });
  }

  /**
   * Envoyer un message
   */
  static async sendMessage(payload: Omit<MessagePayload, 'id' | 'timestamp'>): Promise<boolean> {
    try {
      const service = this.getInstance();

      const fullPayload: MessagePayload = {
        ...payload,
        id: Date.now(),
        timestamp: new Date().toISOString(),
        encrypted: true,
      };

      // Appeler l'API backend (format REST)
      const response = await ApiService.getInstance().post<{
        status: string;
        data?: unknown;
      }>('/api/messaging', {
        receiverId: String(fullPayload.receiverId),
        content: fullPayload.content,
        messageType: fullPayload.messageType ?? 'TEXT',
      });

      if (response.status === 'success') {
        // Émettre localement pour mise à jour immédiate
        service.emitLocal(SOCKET_EVENTS.MESSAGE_SEND, fullPayload);
        return true;
      }

      return false;
    } catch (error) {
      logger.error('❌ Erreur envoi message', { error, payload });
      return false;
    }
  }

  /**
   * Créer une notification locale
   */
  static createNotification(payload: Omit<NotificationPayload, 'id'>): string {
    try {
      const service = this.getInstance();

      const notification: NotificationPayload = {
        ...payload,
        id: Date.now().toString(),
      };

      service.notifications.unshift(notification);
      service.saveToIndexedDB(notification);
      service.emitLocal(SOCKET_EVENTS.NOTIFICATION_NEW, notification);

      logger.info('✅ Notification créée localement', {
        notificationId: notification.id,
        type: payload.type,
      });

      return notification.id;
    } catch (error) {
      logger.error('❌ Erreur création notification', { error, payload });
      return '';
    }
  }

  /**
   * Mettre à jour le statut de présence
   */
  static updatePresence(status: UserPresencePayload['status'], role?: string): void {
    try {
      const service = this.getInstance();

      if (!service.userId) return;

      let currentRole = role;
      if (!currentRole && typeof window !== 'undefined') {
         try {
           const userData = localStorage.getItem('ekose_user_data') || localStorage.getItem('user') || localStorage.getItem('currentUser');
           if (userData) {
             currentRole = JSON.parse(userData).role;
           }
         } catch (e) { logger.debug('Erreur lors de la récupération du rôle utilisateur', { error: e }); }
      }

      const presencePayload: UserPresencePayload = {
        userId: service.userId,
        status,
        role: currentRole || 'USER',
        lastSeen: new Date().toISOString(),
      };

      service.socket?.emit(SOCKET_EVENTS.USER_ONLINE, presencePayload);
      service.emitLocal(SOCKET_EVENTS.USER_ONLINE, presencePayload);
    } catch (error) {
      logger.error('❌ Erreur mise à jour présence', { error, status });
    }
  }

  /**
   * Initialiser un appel
   */
  static async initiateCall(payload: Omit<CallPayload, 'timestamp'>): Promise<boolean> {
    try {
      const service = this.getInstance();

      const fullPayload: CallPayload = {
        ...payload,
        timestamp: new Date().toISOString(),
      };

      service.socket?.emit(SOCKET_EVENTS.CALL_START, fullPayload);
      service.emitLocal(SOCKET_EVENTS.CALL_START, fullPayload);

      return true;
    } catch (error) {
      logger.error('❌ Erreur initiation appel', { error, payload });
      return false;
    }
  }

  /**
   * Marquer une notification comme lue
   */
  static async markNotificationAsRead(notificationId: string): Promise<boolean> {
    try {
      const service = this.getInstance();

      // Mettre à jour localement
      const notification = service.notifications.find((n) => n.id === notificationId);
      if (notification) {
        notification.read = true;
        service.saveToIndexedDB(notification);
      }

      // Appeler l'API backend
      await ApiService.getInstance().put(`/api/notifications/${notificationId}/read`);

      service.emitLocal(SOCKET_EVENTS.NOTIFICATION_READ, { notificationId });
      return true;
    } catch (error) {
      logger.error('❌ Erreur marquage notification lue', { error, notificationId });
      return false;
    }
  }

  /**
   * Supprimer une notification (dismiss)
   */
  static async dismissNotification(notificationId: string): Promise<boolean> {
    try {
      const service = this.getInstance();

      // Mettre à jour localement
      service.notifications = service.notifications.filter((n) => n.id !== notificationId);
      
      // Supprimer de IndexedDB
      if ('indexedDB' in window) {
        const request = indexedDB.open('EkoseNotifications', 1);
        request.onsuccess = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (db.objectStoreNames.contains('notifications')) {
            const transaction = db.transaction(['notifications'], 'readwrite');
            const store = transaction.objectStore('notifications');
            store.delete(notificationId);
          }
        };
      }

      // Appeler l'API backend
      await ApiService.getInstance().delete(`/api/notifications/${notificationId}`);

      service.emitLocal('notification:dismissed', { notificationId });
      return true;
    } catch (error) {
      logger.error('❌ Erreur suppression notification', { error, notificationId });
      return false;
    }
  }

  /**
   * Obtenir toutes les notifications
   */
  static getNotifications(): NotificationPayload[] {
    const service = this.getInstance();
    return service.notifications;
  }

  /**
   * Charger les notifications depuis le backend API et synchroniser localement
   */
  static async fetchNotifications(): Promise<NotificationPayload[]> {
    try {
      const service = this.getInstance();
      const response = await ApiService.getInstance().getNorm<{
        status: string;
        data?: RawNotificationResponse[];
      }>('/api/notifications');

      if (response.status === 'success' && Array.isArray(response.data)) {
        const fetched = response.data.map((n: RawNotificationResponse) => ({
          id: String(n.id),
          userId: service.userId || 0,
          title: n.title,
          message: n.message,
          type: n.type?.toLowerCase() as 'info' | 'success' | 'warning' | 'error',
          read: n.read,
          createdAt: n.createdAt,
        }));

        service.notifications = fetched;

        // Sauvegarder dans IndexedDB
        for (const notif of fetched) {
          service.saveToIndexedDB(notif);
        }

        logger.info('✅ Notifications récupérées depuis le backend et synchronisées', {
          count: fetched.length,
        });

        // Déclencher un événement local nul pour forcer le sync de l'UI
        service.emitLocal(SOCKET_EVENTS.NOTIFICATION_NEW, null);

        return fetched;
      }
      return [];
    } catch (error) {
      logger.error('❌ Erreur lors de la récupération des notifications depuis le backend', { error });
      return [];
    }
  }

  /**
   * Obtenir les notifications non lues
   */
  static getUnreadNotifications(): NotificationPayload[] {
    const service = this.getInstance();
    return service.notifications.filter((n) => !n.read);
  }

  /**
   * Obtenir le nombre de notifications non lues
   */
  static getUnreadCount(): number {
    return this.getUnreadNotifications().length;
  }

  /**
   * Écouter un événement
   */
  static on(event: string, callback: (...args: unknown[]) => void): void {
    const service = this.getInstance();

    if (!service.listeners.has(event)) {
      service.listeners.set(event, []);
    }

    service.listeners.get(event)!.push(callback);
  }

  /**
   * Arrêter d'écouter un événement
   */
  static off(event: string, callback: (...args: unknown[]) => void): void {
    const service = this.getInstance();
    const callbacks = service.listeners.get(event);

    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Configuration des écouteurs socket
   */
  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Messages reçus
    this.socket.on(SOCKET_EVENTS.MESSAGE_RECEIVED, (payload: MessagePayload) => {
      this.handleReceivedMessage(payload);
    });

    // Notifications système
    this.socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, (payload: NotificationPayload) => {
      this.handleReceivedNotification(payload);
    });

    // Mise à jour présence
    this.socket.on(SOCKET_EVENTS.USER_ONLINE, (payload: UserPresencePayload) => {
      this.handlePresenceUpdate(payload);
    });

    // Appels entrants
    this.socket.on(SOCKET_EVENTS.CALL_INCOMING, (payload: CallPayload) => {
      this.handleIncomingCall(payload);
    });

    // Erreurs
    this.socket.on(SOCKET_EVENTS.ERROR, (error: unknown) => {
      logger.error('❌ Erreur socket reçue', { error });
    });

    // Déconnexion
    this.socket.on(SOCKET_EVENTS.DISCONNECT, () => {
      logger.warn('⚠️ Socket déconnecté');
      UnifiedNotificationService.updatePresence('OFFLINE');
    });

    // Reconnexion
    this.socket.on(SOCKET_EVENTS.RECONNECT, () => {
      logger.info('✅ Socket reconnecté');
      UnifiedNotificationService.updatePresence('ONLINE');
    });
  }

  /**
   * Initialiser les écouteurs locaux
   */
  private initializeEventListeners(): void {
    this.listeners.set(SOCKET_EVENTS.MESSAGE_SEND, []);
    this.listeners.set(SOCKET_EVENTS.MESSAGE_RECEIVED, []);
    this.listeners.set(SOCKET_EVENTS.NOTIFICATION_NEW, []);
    this.listeners.set(SOCKET_EVENTS.USER_ONLINE, []);
    this.listeners.set(SOCKET_EVENTS.CALL_INCOMING, []);
  }

  /**
   * Gérer un message reçu
   */
  private handleReceivedMessage(payload: MessagePayload): void {
    try {
      // Déchiffrer si nécessaire
      if (payload.encrypted) {
        payload.encrypted = false;
      }

      this.notifications.unshift({
        id: Date.now().toString(),
        userId: this.userId || 0,
        title: 'Nouveau message',
        message: `Message de l'utilisateur ${payload.senderId}`,
        type: 'info',
        read: false,
        createdAt: payload.timestamp,
      });

      this.saveToIndexedDB(payload);
      this.emitLocal(SOCKET_EVENTS.MESSAGE_RECEIVED, payload);
    } catch (error) {
      logger.error('❌ Erreur traitement message reçu', { error, payload });
    }
  }

  /**
   * Gérer une notification reçue
   */
  private handleReceivedNotification(payload: NotificationPayload): void {
    this.notifications.unshift(payload);
    void this.saveToIndexedDB(payload);
    this.emitLocal(SOCKET_EVENTS.NOTIFICATION_NEW, payload);
  }

  /**
   * Gérer mise à jour présence
   */
  private handlePresenceUpdate(payload: UserPresencePayload): void {
    this.emitLocal(SOCKET_EVENTS.USER_ONLINE, payload);
  }

  /**
   * Gérer appel entrant
   */
  private handleIncomingCall(payload: CallPayload): void {
    const sessionId = payload.consultationId;
    const isValidId = sessionId !== undefined && sessionId !== null && String(sessionId).toLowerCase() !== 'nan' && String(sessionId).trim() !== '';
    
    if (!isValidId) {
        logger.warn('Received invalid CallPayload via socket, skipping:', payload);
        return;
    }
    
    this.emitLocal(SOCKET_EVENTS.CALL_INCOMING, payload);
  }

  /**
   * Émettre un événement local
   */
  private emitLocal(event: string, data: unknown): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          logger.error('❌ Erreur callback événement', { error, event });
        }
      });
    }
  }

  /**
   * Sauvegarder dans IndexedDB
   */
  private async saveToIndexedDB(data: unknown): Promise<void> {
    try {
      if ('indexedDB' in window) {
        const request = indexedDB.open('EkoseNotifications', 2);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains('notifications')) {
            const objectStore = db.createObjectStore('notifications', { keyPath: 'id' });
            objectStore.createIndex('createdAt', 'createdAt', { unique: false });
          }
        };

        request.onsuccess = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (db.objectStoreNames.contains('notifications')) {
            const transaction = db.transaction(['notifications'], 'readwrite');
            const store = transaction.objectStore('notifications');
            store.put(data);
          } else {
            logger.warn('⚠️ Impossible de sauvegarder la notification : object store notifications manquant');
          }
        };
      }
    } catch (error) {
      logger.error('❌ Erreur sauvegarde IndexedDB', { error, data });
    }
  }

  /**
   * Charger depuis IndexedDB
   */
  static async loadFromIndexedDB(): Promise<void> {
    return new Promise((resolve) => {
      try {
        const service = this.getInstance();

        if ('indexedDB' in window) {
          const request = indexedDB.open('EkoseNotifications', 2);

          request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains('notifications')) {
              const objectStore = db.createObjectStore('notifications', { keyPath: 'id' });
              objectStore.createIndex('createdAt', 'createdAt', { unique: false });
            }
          };

          request.onsuccess = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            if (db.objectStoreNames.contains('notifications')) {
              const transaction = db.transaction(['notifications'], 'readonly');
              const store = transaction.objectStore('notifications');
              const getAllRequest = store.getAll();

              getAllRequest.onsuccess = () => {
                service.notifications = getAllRequest.result || [];
                logger.info('✅ Notifications chargées depuis IndexedDB', {
                  count: service.notifications.length,
                });
                resolve();
              };
              getAllRequest.onerror = () => {
                resolve();
              };
            } else {
              resolve();
            }
          };
          request.onerror = () => {
            resolve();
          };
        } else {
          resolve();
        }
      } catch (error) {
        logger.error('❌ Erreur chargement IndexedDB', { error });
        resolve();
      }
    });
  }

  /**
   * Notifier les doctors qu'un patient attend (consolidé de consultationNotifications)
   */
  static async notifyDoctorsPatientWaiting(consultationId: string): Promise<boolean> {
    try {
      const service = this.getInstance();
      // Émettre via socket si disponible
      if (service.socket && service.socket.connected) {
        service.socket.emit('consultation:patient-waiting', { consultationId });
      }
      logger.info('✅ Doctors notifiés pour consultation', { consultationId });
      return true;
    } catch (error) {
      logger.error('❌ Erreur notification doctors', { error, consultationId });
      return false;
    }
  }

  /**
   * S'abonner aux notifications de consultation
   */
  static onConsultationNotification(callback: (notification: unknown) => void): string {
    const id = `consultation_${Date.now()}`;
    this.on('consultation-notification', callback);
    return id;
  }

  /**
   * Se désabonner des notifications de consultation
   */
  static offConsultationNotification(id: string, callback: (notification: unknown) => void): void {
    this.off('consultation-notification', callback);
  }
}
